import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import type {
  UserDetail,
  UserInvite,
  UserUpdate,
} from "@/api/types";

const KEY = ["users"] as const;

interface UseUsersFilters {
  is_active?: boolean;
  is_admin?: boolean;
  q?: string;
  team_id?: string;
}

export function useUsers(filters: UseUsersFilters = {}) {
  return useQuery({
    queryKey: [...KEY, filters],
    queryFn: () => {
      const params: Record<string, string | boolean> = {};
      if (filters.is_active !== undefined) params.is_active = filters.is_active;
      if (filters.is_admin !== undefined) params.is_admin = filters.is_admin;
      if (filters.q) params.q = filters.q;
      if (filters.team_id) params.team_id = filters.team_id;
      return api.GET<UserDetail[]>("/users", { params });
    },
  });
}

export function useUser(userId: string) {
  return useQuery({
    queryKey: [...KEY, userId],
    queryFn: () => api.GET<UserDetail>(`/users/${userId}`),
    enabled: Boolean(userId),
  });
}

export function useUpdateUser(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: UserUpdate) =>
      api.PATCH<UserDetail>(`/users/${userId}`, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      void qc.invalidateQueries({ queryKey: [...KEY, userId] });
      void qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export function useInviteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UserInvite) =>
      api.POST<UserDetail>("/users/invite", payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}
